import { type Brand } from '@/models/account-management/Brand';
import { type Location } from '@/models/account-management/Location';
import { createApiResource } from '../adminApi';
import { HttpError, getJwt } from '../common';

const BASE_URL = import.meta.env.VITE_API_URL;

export const brandApi = {
    ...createApiResource<Brand, string>('account-management/brands'),
    list: async (includeLocations?: boolean): Promise<Brand> => {
        const response = await fetch(`${BASE_URL}/account-management/brands?includeLocations=${includeLocations}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });

        const contentType = response.headers.get('content-type') ?? '';
        const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

        if (!response.ok) throw new HttpError(response.status, data);
        return await data;
    },
    /**
     * An organization's brands, optionally with each brand's locations.
     *
     * `organizationApi.listBrands` cannot do this — that route asks the service for brands without locations
     * and there is no flag to change it. The scope picker needs the locations to work out which brands the
     * signed-in user can actually reach (TTD-160).
     */
    listByOrganization: async (organizationId: string, includeLocations = false): Promise<Brand[]> => {
        const response = await fetch(`${BASE_URL}/account-management/brands?organizationId=${organizationId}&includeLocations=${includeLocations}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data ?? [];
    },
    listLocations: async (id: string): Promise<Location[]> => {
        const response = await fetch(`${BASE_URL}/account-management/brands/${id}/locations`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data ?? [];
    }
};
